import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { makeStyles } from "tss-react/mui";
import { Grid, Paper } from "@mui/material";
import {
  Avatar,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FilterListOutlined,
  ArrowBackIosNew,
  ArrowForwardIos,
  ForumOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  BiotechOutlined,
  HourglassEmptyOutlined,
} from "@mui/icons-material";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TicketHeader from "../../components/TicketHeader";
import TicketInfo from "../../components/TicketInfo";
import MessagesList from "../../components/MessagesList";
import CreateTestCaseDialog from "../../components/CreateTestCaseDialog";
import PeriodSelect from "../../components/PeriodSelect";
import { rangoDePeriodo } from "../../helpers/rangoDePeriodo";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { socketConnection } from "../../services/socket";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";

const CHANNELS = [
  { value: "", label: "Todos" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "sandbox", label: "Sandbox" },
];

const EMPTY_FILTERS = { channel: "", whatsappId: "", search: "", dateFrom: "", dateTo: "" };

const AUDIT_STATUS_INFO = {
  reviewed: { label: "Revisada", color: "#2fae60", icon: CheckCircleOutlined },
  problem: { label: "Problema", color: "#e53935", icon: WarningAmberOutlined },
  problem_reviewed: { label: "Problema (revisado)", color: "#e58a35", icon: WarningAmberOutlined },
};

const PERIODOS_AI_REPLIES = [
  { valor: "hoy", etiqueta: "Hoy" },
  { valor: "ayer", etiqueta: "Ayer" },
  { valor: "semana", etiqueta: "Esta semana" },
  { valor: "semanaPasada", etiqueta: "Semana pasada" },
  { valor: "semanaYPasada", etiqueta: "Esta semana y la pasada" },
  { valor: "mes", etiqueta: "Todo el mes" },
  { valor: "personalizado", etiqueta: "Personalizado" },
];

const BUCKETS = [
  { value: "pending", label: "Pendientes" },
  { value: "good", label: "Bien" },
  { value: "bad", label: "Con problema" },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const useStyles = makeStyles()((theme) => ({
  chatContainer: {
    flex: 1,
    height: `calc(100% - 48px)`,
    overflowY: "hidden",
  },
  chatPapper: {
    display: "flex",
    height: "100%",
  },
  listWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  listScroll: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  row: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px 8px 16px",
    minHeight: 68,
    cursor: "pointer",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      background: theme.palette.action.hover,
    },
    "&.selected": {
      background: theme.palette.action.selected,
    },
  },
  avatar: {
    width: 46,
    height: 46,
    flexShrink: 0,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  rowTopLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  rowName: {
    fontWeight: 600,
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowTime: {
    flexShrink: 0,
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
  },
  rowSubLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  rowSub: {
    fontSize: "0.76rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  pager: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  popoverBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    minWidth: 260,
  },
  popoverActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  auditBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  auditInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
    paddingRight: theme.spacing(1),
  },
  auditStatusChip: {
    flexShrink: 0,
    fontWeight: 600,
  },
  auditComment: {
    fontSize: "0.76rem",
    color: theme.palette.text.secondary,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  problemForm: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flex: 1,
  },
  welcomeMsg: {
    backgroundColor: theme.palette.mode === "light" ? "#eee" : theme.palette.background.default,
    color: theme.palette.text.secondary,
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
  },
  kpiBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: "auto",
  },
  kpiItem: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },
  kpiValue: {
    fontWeight: 700,
    fontSize: "1rem",
    fontVariantNumeric: "tabular-nums",
  },
  kpiLabel: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  periodSelect: {
    background: "transparent",
    color: "inherit",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: "5px 8px",
    fontSize: "0.8125rem",
    cursor: "pointer",
  },
}));

const AiReplyConversation = ({ classes, ticketId, audit, onAuditSaved }) => {
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [savingAudit, setSavingAudit] = useState(false);
  const [showProblemForm, setShowProblemForm] = useState(false);
  const [problemComment, setProblemComment] = useState("");
  const [showTestCaseDialog, setShowTestCaseDialog] = useState(false);

  useEffect(() => {
    setShowProblemForm(false);
    setProblemComment("");
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);

    const fetchTicket = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}`);
        setContact(data.contact);
        setTicket(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [ticketId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on("connect", () => socket.emit("joinChatBox", `${ticketId}`));
    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "update") setTicket(data.ticket);
    });
    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        setContact((prev) => (prev.id === data.contact?.id ? { ...prev, ...data.contact } : prev));
      }
    });

    return () => socket.disconnect();
  }, [ticketId]);

  const saveAudit = async (status, comment) => {
    setSavingAudit(true);
    try {
      const { data } = await api.put(`/ai-replies/${ticketId}/audit`, {
        status,
        comment: comment || null,
      });
      toast.success(status === "reviewed" ? "Marcada como revisada." : "Problema reportado.");
      onAuditSaved(ticketId, data);
      setShowProblemForm(false);
      setProblemComment("");
    } catch (err) {
      toastError(err);
    } finally {
      setSavingAudit(false);
    }
  };

  const statusInfo = audit?.status
    ? AUDIT_STATUS_INFO[audit.status]
    : { label: "Sin revisar", color: "#8c96aa", icon: HourglassEmptyOutlined };
  const StatusIcon = statusInfo.icon;

  return (
    <>
      <TicketHeader loading={loading}>
        {ticket.user !== undefined && (
          <TicketInfo contact={contact} ticket={ticket} onClick={() => {}} />
        )}

        {!showProblemForm && (
          <div className={classes.headerActions}>
            {}
            {audit?.status !== "reviewed" && audit?.status !== "problem_reviewed" && (
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<CheckCircleOutlined fontSize="small" />}
                disabled={savingAudit}
                onClick={() => saveAudit("reviewed", audit?.comment)}
              >
                Marcar revisada
              </Button>
            )}
            {audit?.status !== "problem" && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<WarningAmberOutlined fontSize="small" />}
                disabled={savingAudit}
                onClick={() => setShowProblemForm(true)}
              >
                Reportar problema
              </Button>
            )}

            <Divider orientation="vertical" flexItem style={{ margin: "4px 0" }} />

            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<BiotechOutlined fontSize="small" />}
              onClick={() => setShowTestCaseDialog(true)}
            >
              Crear caso de prueba
            </Button>
          </div>
        )}

        {ticket.uuid && (
          <Tooltip title="Abrir en la bandeja">
            <IconButton
              size="small"
              component="a"
              href={`/tickets/${ticket.uuid}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ alignSelf: "center", marginRight: 8 }}
            >
              <ForumOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </TicketHeader>

      <div className={classes.auditBar}>
        {showProblemForm ? (
          <div className={classes.problemForm}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="¿Qué problema tiene esta respuesta?"
              value={problemComment}
              onChange={(e) => setProblemComment(e.target.value)}
            />
            <Button size="small" onClick={() => setShowProblemForm(false)} disabled={savingAudit}>
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={!problemComment.trim() || savingAudit}
              onClick={() => saveAudit("problem", problemComment.trim())}
            >
              Guardar
            </Button>
          </div>
        ) : (
          <div className={classes.auditInfo}>
            <Chip
              size="small"
              variant="outlined"
              icon={<StatusIcon fontSize="small" style={{ color: statusInfo.color }} />}
              label={statusInfo.label}
              className={classes.auditStatusChip}
              style={{ borderColor: statusInfo.color, color: statusInfo.color }}
            />
            {audit?.comment && (
              <Tooltip title={audit.comment}>
                <Typography className={classes.auditComment}>
                  <strong>Motivo:</strong> {audit.comment}
                </Typography>
              </Tooltip>
            )}
            {audit?.auditedBy && (
              <Typography className={classes.auditComment}>
                <strong>Calificó:</strong> {audit.auditedBy}
              </Typography>
            )}
          </div>
        )}
      </div>

      <ReplyMessageProvider>
        <MessagesList ticket={ticket} ticketId={ticket.id} isGroup={ticket.isGroup} />
      </ReplyMessageProvider>

      <CreateTestCaseDialog
        open={showTestCaseDialog}
        onClose={() => setShowTestCaseDialog(false)}
        ticketId={ticketId}
        defaultTitle={contact?.name ? `${contact.name} - problema real` : ""}
      />
    </>
  );
};

const AiReplies = () => {
  const { classes } = useStyles();

  const [view, setView] = useState("pending");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  const [connections, setConnections] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, good: 0, bad: 0 });

  const [periodo, setPeriodo] = useState("hoy");
  const [filters, setFilters] = useState(() => {
    const rango = rangoDePeriodo("hoy");
    return { ...EMPTY_FILTERS, dateFrom: rango.from.format("YYYY-MM-DD"), dateTo: rango.to.format("YYYY-MM-DD") };
  });
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);

  const [filterAnchor, setFilterAnchor] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/whatsapp/");
        setConnections((data || []).filter((c) => c.channel === "whatsapp" || c.channel === "sandbox"));
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { pageNumber: page, bucket: view };
      Object.keys(filters).forEach((k) => {
        if (filters[k]) params[k] = filters[k];
      });

      const { data } = await api.get("/ai-replies", { params });
      setRows(data.rows || []);
      setTotal(data.count || 0);
      setHasMore(Boolean(data.hasMore));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [page, filters, view]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      Object.keys(filters).forEach((k) => {
        if (filters[k]) params[k] = filters[k];
      });
      const { data } = await api.get("/ai-replies/summary", { params });
      setSummary({ pending: data.pending || 0, good: data.good || 0, bad: data.bad || 0 });
    } catch (err) {
      toastError(err);
    }
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handlePeriodo = (valor) => {
    setPeriodo(valor);
    setPage(1);
    setSelectedTicketId(null);
    if (valor === "personalizado") return;

    const rango = rangoDePeriodo(valor);
    setFilters((f) => ({ ...f, dateFrom: rango.from.format("YYYY-MM-DD"), dateTo: rango.to.format("YYYY-MM-DD") }));
  };

  const changeView = (newView) => {
    if (newView === view) return;
    setView(newView);
    setPage(1);
    setSelectedTicketId(null);
  };

  const bucketOfStatus = (status) => {
    if (status === "reviewed") return "good";
    if (status === "problem" || status === "problem_reviewed") return "bad";
    return "pending";
  };

  const handleAuditSaved = (ticketId, audit) => {
    if (bucketOfStatus(audit.status) !== view) {
      setRows((prev) => prev.filter((r) => r.ticketId !== ticketId));
      setTotal((prev) => Math.max(0, prev - 1));
      fetchSummary();
      return;
    }

    setRows((prev) =>
      prev.map((r) =>
        r.ticketId === ticketId ? { ...r, auditStatus: audit.status, auditComment: audit.comment } : r
      )
    );
    fetchSummary();
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));

  const openFilterPopover = (e) => {
    setDraftFilters(filters);
    setFilterAnchor(e.currentTarget);
  };

  const applyFilters = () => {
    setFilters((f) => ({
      ...f,
      channel: draftFilters.channel,
      whatsappId: draftFilters.whatsappId,
      search: draftFilters.search,
    }));
    setPage(1);
    setFilterAnchor(null);
  };

  const clearFilters = () => {
    setFilters((f) => ({ ...f, channel: "", whatsappId: "", search: "" }));
    setDraftFilters((f) => ({ ...f, channel: "", whatsappId: "", search: "" }));
    setPage(1);
    setFilterAnchor(null);
  };

  const applyDateRange = () => {
    setFilters((f) => ({ ...f, dateFrom: draftFilters.dateFrom, dateTo: draftFilters.dateTo }));
    setPage(1);
    setDateAnchor(null);
  };

  const clearDateRange = () => {
    setFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }));
    setDraftFilters((f) => ({ ...f, dateFrom: "", dateTo: "" }));
    setPage(1);
    setDateAnchor(null);
  };

  const activeFilterCount = ["channel", "whatsappId", "search"].filter((k) => filters[k]).length;

  const revisados = summary.good + summary.bad;
  const goodPct = revisados > 0 ? Math.round((summary.good / revisados) * 100) : null;
  const veredicto =
    revisados === 0
      ? { texto: "Sin revisar todavía", color: "#8c96aa" }
      : summary.good > summary.bad
      ? { texto: `Vamos bien · ${goodPct}% bien`, color: "#2fae60" }
      : summary.bad > summary.good
      ? { texto: `Vamos mal · ${goodPct}% bien`, color: "#e53935" }
      : { texto: `Parejo · ${goodPct}% bien`, color: "#e5a935" };

  return (
    <MainContainer maxWidth={false}>
      <MainHeader>
        <Title>Auditoría de IA</Title>
        <div className={classes.kpiBar}>
          <Chip
            size="small"
            variant="outlined"
            label={veredicto.texto}
            style={{ borderColor: veredicto.color, color: veredicto.color, fontWeight: 700 }}
          />
          <div className={classes.kpiItem}>
            <span className={classes.kpiValue} style={{ color: "#8c96aa" }}>{summary.pending}</span>
            <span className={classes.kpiLabel}>Pendientes</span>
          </div>
          <div className={classes.kpiItem}>
            <span className={classes.kpiValue} style={{ color: "#2fae60" }}>{summary.good}</span>
            <span className={classes.kpiLabel}>Bien</span>
          </div>
          <div className={classes.kpiItem}>
            <span className={classes.kpiValue} style={{ color: "#e53935" }}>{summary.bad}</span>
            <span className={classes.kpiLabel}>Con problema</span>
          </div>
        </div>
      </MainHeader>

      <div className={classes.chatContainer}>
        <div className={classes.chatPapper}>
          <Grid container spacing={0} style={{ height: "100%", width: "100%" }}>
            <Grid className={classes.listWrapper} size={4}>
              <Tabs
                value={view}
                onChange={(_, v) => changeView(v)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
              >
                {BUCKETS.map((b) => (
                  <Tab key={b.value} value={b.value} label={b.label} sx={{ minHeight: 40 }} />
                ))}
              </Tabs>

              <div className={classes.toolbar}>
                <PeriodSelect
                  className={classes.periodSelect}
                  value={periodo}
                  onChange={handlePeriodo}
                  opciones={PERIODOS_AI_REPLIES}
                />

                {periodo === "personalizado" && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      setDraftFilters(filters);
                      setDateAnchor(e.currentTarget);
                    }}
                  >
                    {filters.dateFrom || "…"} – {filters.dateTo || "…"}
                  </Button>
                )}

                <Button
                  size="small"
                  variant={activeFilterCount > 0 ? "contained" : "outlined"}
                  color={activeFilterCount > 0 ? "primary" : undefined}
                  startIcon={<FilterListOutlined fontSize="small" />}
                  onClick={openFilterPopover}
                >
                  Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </Button>
              </div>

              <Popover
                open={Boolean(dateAnchor)}
                anchorEl={dateAnchor}
                onClose={() => setDateAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <div className={classes.popoverBody}>
                  <TextField
                    label="Desde"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={draftFilters.dateFrom}
                    onChange={(e) => setDraftFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  />
                  <TextField
                    label="Hasta"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={draftFilters.dateTo}
                    onChange={(e) => setDraftFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  />
                  <div className={classes.popoverActions}>
                    <Button size="small" onClick={clearDateRange}>Limpiar</Button>
                    <Button size="small" variant="contained" onClick={applyDateRange}>Aplicar</Button>
                  </div>
                </div>
              </Popover>

              <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <div className={classes.popoverBody}>
                  <TextField
                    select
                    label="Canal"
                    size="small"
                    value={draftFilters.channel}
                    onChange={(e) => setDraftFilters((f) => ({ ...f, channel: e.target.value }))}
                  >
                    {CHANNELS.map((c) => (
                      <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Conexión"
                    size="small"
                    value={draftFilters.whatsappId}
                    onChange={(e) => setDraftFilters((f) => ({ ...f, whatsappId: e.target.value }))}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {connections.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Buscar"
                    size="small"
                    placeholder="Contacto, número o texto..."
                    value={draftFilters.search}
                    onChange={(e) => setDraftFilters((f) => ({ ...f, search: e.target.value }))}
                  />
                  <div className={classes.popoverActions}>
                    <Button size="small" onClick={clearFilters}>Limpiar</Button>
                    <Button size="small" variant="contained" onClick={applyFilters}>Aplicar</Button>
                  </div>
                </div>
              </Popover>

              <div className={classes.listScroll}>
                {!loading && rows.length === 0 ? (
                  <Typography className={classes.empty}>
                    {view === "bad"
                      ? "Ningún ticket con problema reportado en este período."
                      : view === "good"
                      ? "Ninguna respuesta revisada como bien en este período."
                      : "Ningún ticket pendiente de auditar en este período."}
                  </Typography>
                ) : (
                  rows.map((r) => {
                    const statusInfo = r.auditStatus ? AUDIT_STATUS_INFO[r.auditStatus] : null;
                    const StatusIcon = statusInfo?.icon;
                    return (
                      <div
                        key={r.ticketId}
                        className={`${classes.row} ${r.ticketId === selectedTicketId ? "selected" : ""}`}
                        onClick={() => setSelectedTicketId(r.ticketId)}
                      >
                        <Avatar className={classes.avatar} src={r.contactAvatar || ""} />
                        <div className={classes.rowBody}>
                          <div className={classes.rowTopLine}>
                            <span className={classes.rowName}>
                              {statusInfo && (
                                <Tooltip
                                  title={r.auditComment ? `${statusInfo.label}: ${r.auditComment}` : statusInfo.label}
                                >
                                  <StatusIcon
                                    style={{ fontSize: 14, color: statusInfo.color, verticalAlign: "text-bottom", marginRight: 4 }}
                                  />
                                </Tooltip>
                              )}
                              {r.contactName || "—"}
                            </span>
                            <span className={classes.rowTime}>{formatDate(r.lastReplyAt)}</span>
                          </div>
                          <div className={classes.rowSubLine}>
                            <span className={classes.rowSub}>
                              {r.channel === "sandbox" ? "Sandbox" : "WhatsApp"} · {r.connection || "—"}
                              {r.queue ? ` · ${r.queue}` : ""}
                            </span>
                            <Chip size="small" variant="outlined" label={r.replyCount} />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className={classes.footer}>
                <span>Página {page} de {totalPages} · Total: {total.toLocaleString("es-EC")}</span>

                <div className={classes.pager}>
                  <IconButton size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ArrowBackIosNew style={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" disabled={!hasMore} onClick={() => setPage((p) => p + 1)}>
                    <ArrowForwardIos style={{ fontSize: 14 }} />
                  </IconButton>
                </div>
              </div>
            </Grid>

            <Grid className={classes.messagesWrapper} size={8}>
              {selectedTicketId ? (
                <AiReplyConversation
                  classes={classes}
                  ticketId={selectedTicketId}
                  audit={{
                    status: rows.find((r) => r.ticketId === selectedTicketId)?.auditStatus || null,
                    comment: rows.find((r) => r.ticketId === selectedTicketId)?.auditComment || null,
                    auditedBy: rows.find((r) => r.ticketId === selectedTicketId)?.auditedBy || null,
                  }}
                  onAuditSaved={handleAuditSaved}
                />
              ) : (
                <Paper square variant="outlined" className={classes.welcomeMsg}>
                  <span>Elegí un ticket para ver la conversación</span>
                </Paper>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    </MainContainer>
  );
};

export default AiReplies;
